import React, { useState } from "react";
import { useProgression } from "../../../context/ProgressionContext";

type Step = {
  title: string;
  content: React.ReactNode;
};

export function MicroCoursBiaisConfirmation() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  const { isGuest, markMicroCourseCompleted } = useProgression();

  const steps: Step[] = [
    {
      title: "Qu’est-ce que le biais de confirmation ?",
      content: (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          C’est la tendance à chercher, croire ou mémoriser plus facilement les informations qui confirment ce que l’on pense déjà,
          tout en ignorants ou minimisant celles qui contredisent nos idées.
        </p>
      ),
    },
    {
      title: "Un exemple du quotidien",
      content: (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Tu es persuadé qu’un produit est « le meilleur ». Tu lis surtout les avis qui vont dans ton sens, tu zappes les critiques,
          et tu cites les commentaires positifs pour prouver que tu as raison.
        </p>
      ),
    },
    {
      title: "Et toi ?",
      content: (
        <div className="text-sm text-slate-600 dark:text-slate-300 space-y-2">
          <p>Quand as-tu déjà cherché des infos qui allaient dans ton sens ?</p>
          <p>
            Si tu tombes sur deux articles opposés, lequel lis-tu en premier ? Pourquoi ?
          </p>
          <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
            Question simple : es-tu prêt à lire un avis contraire jusqu’au bout ? (Tu peux juste y réfléchir et noter « oui / non » pour toi-même.)
          </div>
        </div>
      ),
    },
    {
      title: "Comment le repérer et le limiter ?",
      content: (
        <ul className="list-disc ml-4 text-sm text-slate-600 dark:text-slate-300 space-y-2">
          <li>Observe tes réactions : quand un contenu te contredit, le rejettes-tu immédiatement ?</li>
          <li>Va chercher au moins une source opposée avant de conclure.</li>
          <li>Pose-toi une question clé : « Qu’est-ce qui pourrait me faire changer d’avis ? »</li>
        </ul>
      ),
    },
  ];

  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  function handleNext() {
    if (isLast) {
      setCompleted(true);
      return;
    }
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  }

  function handlePrev() {
    setCompleted(false);
    setCurrentStep((s) => Math.max(s - 1, 0));
  }

  function handleRestart() {
    setCompleted(false);
    setCurrentStep(0);
  }

  function handleMarkDone() {
    if (isGuest) {
      alert("Creez un compte ou connectez-vous pour enregistrer votre progression.");
      return;
    }
    markMicroCourseCompleted("micro-biais-1", "biais-confirmation", ["mod-biais-confirmation-1"]);
  }

  const activeStep = steps[currentStep];

  return (
    <div className="max-w-xl mx-auto mm-surface p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Micro-cours interactif
          </p>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Comprendre le biais de confirmation
          </h2>
        </div>
        <span className="text-[11px] rounded-full px-3 py-1 border border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
          Étape {currentStep + 1} / {steps.length}
        </span>
      </div>

      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {activeStep.title}
        </h3>
        {activeStep.content}
      </div>

      {completed && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-300/50 dark:bg-emerald-900/20 dark:text-emerald-100">
          Micro-cours terminé 👌 Prêt à le rejouer ou à passer à un autre exercice.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-6">
        <button
          type="button"
          onClick={handlePrev}
          disabled={isFirst}
          className={`px-3 py-1.5 rounded-full border text-sm font-medium ${
            isFirst
              ? "border-slate-200 text-slate-400 bg-slate-100 cursor-not-allowed dark:border-slate-700 dark:text-slate-500 dark:bg-slate-800"
              : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
          }`}
        >
          Précédent
        </button>

        {!isLast && (
          <button
            type="button"
            onClick={handleNext}
            className="px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500"
          >
            Suivant
          </button>
        )}

        {isLast && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleNext}
              className="px-4 py-1.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500"
            >
              Terminer
            </button>
            <button
              type="button"
              onClick={handleRestart}
              className="px-3 py-1.5 rounded-full border border-slate-300 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              Rejouer
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-2 text-sm">
        <button
          type="button"
          onClick={handleMarkDone}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-60"
          disabled={!completed}
        >
          J’ai termine
        </button>
        {isGuest && (
          <p className="text-[11px] text-slate-500">
            Connectez-vous pour sauvegarder votre progression.
          </p>
        )}
      </div>
    </div>
  );
}

